import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "Store/configureStore";

import { createInitialOrganizationState } from "./projectCreation.types";

import type {
  FormattedGithubOrganization,
  FormattedGithubRepository,
  OrganizationProjectCreationState
} from "./projectCreation.types";

// ============================================================================
// Constants
// ============================================================================

/**
 * Module-level constant for default organization state.
 * Using a constant instead of calling createInitialOrganizationState() in selectors
 * ensures stable references and proper memoization with createSelector.
 *
 * Shallow freeze catches the most obvious mutation mistakes at the top level.
 * Nested objects remain mutable, but Redux selectors shouldn't mutate results anyway.
 */
const DEFAULT_ORGANIZATION_STATE = Object.freeze(
  createInitialOrganizationState()
);

// ============================================================================
// Base Selectors
// ============================================================================

const selectProjectCreationSlice = (state: RootState) => state.projectCreation;

/**
 * `undefined` is a state every caller passes through: the URL carries the
 * organization *name*, and resolving it to an id waits on the organizations
 * list. Accepting it here is what lets a component select unconditionally
 * instead of writing its own fallback for those first few renders.
 */
const selectOrganizationId = (
  _: RootState,
  params: { organizationId: string | undefined }
) => params.organizationId;

// ============================================================================
// Organization State Selector
// ============================================================================

/**
 * Selects the project creation state for a specific organization.
 * Returns initial state if the organization doesn't exist, or isn't known yet.
 */
export const selectOnboardingByOrganization = createSelector(
  [selectProjectCreationSlice, selectOrganizationId],
  (projectCreation, organizationId): OrganizationProjectCreationState => {
    return (
      (organizationId
        ? projectCreation.byOrganizationId[organizationId]
        : undefined) ?? DEFAULT_ORGANIZATION_STATE
    );
  }
);

// ============================================================================
// Wizard Selectors
// ============================================================================

export const selectWizardState = createSelector(
  [selectOnboardingByOrganization],
  orgState => orgState.wizard
);

export const selectOnboardingType = createSelector(
  [selectWizardState],
  wizard => wizard.onboardingType
);

// ============================================================================
// Project Details Selectors
// ============================================================================

export const selectProjectDetails = createSelector(
  [selectOnboardingByOrganization],
  orgState => orgState.projectDetails
);

export const selectProjectName = createSelector(
  [selectProjectDetails],
  details => details.projectName
);

export const selectProductionEnvironment = createSelector(
  [selectProjectDetails],
  details => details.productionEnvironment
);

export const selectSelectedRegion = createSelector(
  [selectProjectDetails],
  details => details.region
);

// ============================================================================
// Git Provider Selectors
// ============================================================================

export const selectGitProviderState = createSelector(
  [selectOnboardingByOrganization],
  orgState => orgState.gitProvider
);

export const selectGitProvider = createSelector(
  [selectGitProviderState],
  gitProvider => gitProvider.provider
);

export const selectGitProviderLoading = createSelector(
  [selectGitProviderState],
  gitProvider => gitProvider.isLoading
);

export const selectGitProviderError = createSelector(
  [selectGitProviderState],
  gitProvider => gitProvider.error
);

// ============================================================================
// GitHub-Specific Selectors
// ============================================================================

export const selectGithubState = createSelector(
  [selectGitProviderState],
  gitProvider => gitProvider.github
);

export const selectGithubOrganizations = createSelector(
  [selectGithubState],
  github => github.organizations
);

export const selectHasFetchedGithubOrganizations = createSelector(
  [selectGithubState],
  github => github.hasFetchedOrganizations
);

export const selectSelectedGithubOrganizationId = createSelector(
  [selectGithubState],
  github => github.selectedOrganizationId
);

export const selectSelectedGithubOrganizationLabel = createSelector(
  [selectGithubState],
  github => github.selectedOrganizationLabel
);

export const selectGithubRepositories = createSelector(
  [selectGithubState],
  github => github.repositories
);

export const selectSelectedGithubRepository = createSelector(
  [selectGithubState],
  github => github.selectedRepository
);

export const selectSelectedGithubRepositoryDefaultBranch = createSelector(
  [selectGithubState],
  github => github.selectedRepositoryDefaultBranch
);

export const selectGithubBranches = createSelector(
  [selectGithubState],
  github => github.branches
);

export const selectSelectedGithubBranch = createSelector(
  [selectGithubState],
  github => github.selectedBranch
);

export const selectGithubInstallationId = createSelector(
  [selectGithubState],
  github => github.installationId
);

// ============================================================================
// Formatted Options Selectors (for Select components)
// ============================================================================

/**
 * Returns GitHub organizations formatted for Select component.
 */
export const selectGithubOrganizationOptions = createSelector(
  [selectGithubOrganizations],
  (organizations): FormattedGithubOrganization[] =>
    organizations.map(org => ({
      // Use org.id (internal record ID), not gh_installation_id (GitHub's ID).
      // The API expects the internal ID for fetching repos and branches.
      value: org.id,
      label: org.gh_organization_name
    }))
);

/**
 * Returns GitHub repositories formatted for Select component.
 */
export const selectGithubRepositoryOptions = createSelector(
  [selectGithubRepositories],
  (repositories): FormattedGithubRepository[] =>
    repositories.map(repo => ({
      value: repo.name,
      label: repo.name,
      defaultBranch: repo.default_branch
    }))
);

/**
 * Returns GitHub branches formatted for Select component.
 */
export const selectGithubBranchOptions = createSelector(
  [selectGithubBranches],
  branches =>
    branches.map(branch => ({
      value: branch.name,
      label: branch.name
    }))
);

// ============================================================================
// Verification Selectors
// ============================================================================

export const selectVerificationState = createSelector(
  [selectOnboardingByOrganization],
  orgState => orgState.verification
);

export const selectVerificationType = createSelector(
  [selectVerificationState],
  verification => verification.type
);

export const selectVerificationCredentials = createSelector(
  [selectVerificationState],
  verification => verification.credentials
);

// ============================================================================
// Project Creation Selectors
// ============================================================================

export const selectProjectCreationState = createSelector(
  [selectOnboardingByOrganization],
  orgState => orgState.projectCreation
);

export const selectProjectCreationLoading = createSelector(
  [selectProjectCreationState],
  creation => creation.isLoading
);

export const selectHasCreatedProject = createSelector(
  [selectProjectCreationState],
  creation => creation.hasCreatedProject
);

export const selectProjectCreationErrorType = createSelector(
  [selectProjectCreationState],
  creation => creation.errorType
);
